import { createContext, useContext, useMemo } from "react";
import type { PageKey } from "@/lib/rbac/pages";

const VisiblePagesContext = createContext<ReadonlySet<PageKey>>(new Set<PageKey>());

/**
 * The set of pages the signed-in user may view, resolved once per request in the
 * dashboard layout and handed to client components.
 *
 * Pages that left the nav bar are now tabs inside another page, so the page
 * hosting the tab has to know whether this user is allowed the tab at all. The
 * API routes still enforce the same permission — this only decides whether a
 * dead tab is drawn.
 */
export function VisiblePagesProvider({
  pageKeys,
  children,
}: {
  pageKeys: PageKey[];
  children: React.ReactNode;
}) {
  const value = useMemo(() => new Set(pageKeys), [pageKeys]);
  return (
    <VisiblePagesContext.Provider value={value}>{children}</VisiblePagesContext.Provider>
  );
}

export function useCanViewPage(key: PageKey): boolean {
  return useVisiblePages().has(key);
}

/**
 * The whole visible set, for callers that must test MANY keys in one render.
 *
 * `useCanViewPage` is a hook, so it cannot be called from inside a `.filter()`
 * callback — the number of hook calls would then vary with the length of the
 * list, which React forbids. Anything filtering a registry slice (the section
 * nav, a tab bar) reads the set once and tests it as plain data.
 */
export function useVisiblePages(): ReadonlySet<PageKey> {
  return useContext(VisiblePagesContext);
}
